import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { assignBranchInventoryItem } from "@/integrations/supabase/inventory-services";
import { BranchInventoryWithRelations } from "@/types/inventory";

interface AssignToOperatorModalProps {
  isOpen: boolean;
  onClose: () => void;
  inventoryItem: BranchInventoryWithRelations | null;
  onSuccess: () => void;
}

const AssignToOperatorModal = ({ isOpen, onClose, inventoryItem, onSuccess }: AssignToOperatorModalProps) => {
  const [selectedOperatorId, setSelectedOperatorId] = useState<string>("");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: operators, isLoading: isLoadingOperators } = useQuery({
    queryKey: ['branch-operators', inventoryItem?.branch_id],
    queryFn: async () => {
      if (!inventoryItem?.branch_id) return [];
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name')
        .eq('branch_id', inventoryItem.branch_id)
        .eq('role', 'operator');
      
      if (error) throw error;
      return data;
    },
    enabled: !!inventoryItem?.branch_id,
  });

  const assignMutation = useMutation({
    mutationFn: ({ inventoryId, operatorId }: { inventoryId: string; operatorId: string }) =>
      assignBranchInventoryItem(inventoryId, operatorId),
    onSuccess: () => {
      toast({
        title: "¡Éxito!",
        description: "Repuesto asignado correctamente.",
      });
      queryClient.invalidateQueries({ queryKey: ['branch-inventory'] });
      onSuccess();
      handleClose();
    },
    onError: (error) => {
      console.error("Error assigning item:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Error al asignar el repuesto.",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOperatorId || !inventoryItem) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Por favor selecciona un operador.",
      });
      return;
    }

    assignMutation.mutate({
      inventoryId: inventoryItem.id,
      operatorId: selectedOperatorId
    });
  };

  const handleClose = () => {
    setSelectedOperatorId("");
    onClose();
  };

  // Pre-select current assignee if opening
  // We could use useEffect here to set selectedOperatorId when inventoryItem changes
  // but for simplicity, let's just let the user select fresh. 
  // Actually, UX is better if we show who it is currently assigned to.
  // Let's try to set it via default value logic or effect if strictly needed, 
  // but since we clear state on close, a simple effect is fine.
  
  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Asignar Repuesto a Operador</DialogTitle>
        </DialogHeader>

        <div className="py-4">
          <div className="mb-4">
            <p className="text-sm text-gray-500">Repuesto:</p>
            <p className="font-medium">{inventoryItem?.spare_part_type?.name}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="operator">Operador</Label>
              <Select value={selectedOperatorId} onValueChange={setSelectedOperatorId}>
                <SelectTrigger>
                  <SelectValue placeholder={isLoadingOperators ? "Cargando..." : "Selecciona un operador"} />
                </SelectTrigger>
                <SelectContent>
                  {operators?.map((op) => (
                    <SelectItem key={op.id} value={op.id}>
                      {op.full_name || "Operador sin nombre"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancelar
              </Button>
              <Button type="submit" disabled={assignMutation.isPending || !selectedOperatorId}>
                {assignMutation.isPending ? "Asignando..." : "Asignar"}
              </Button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AssignToOperatorModal;
